import { useEffect, useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import isError from 'lodash/isError'
import omit from 'lodash/omit'
import { Link, useNavigate } from 'react-router'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { ROUTE_DOCUMENTS, ROUTE_DOCUMENT_REVIEW, isUnderwayDocumentProcessingStatus } from '@klear/constants'
import type { ContributionAmountKind } from '@klear/constants/document-processing'
import { decodeMoneyWire } from '@klear/money'
import type { ReviewerHints } from '@klear/schema/document-processing'
import type { DocumentStatus } from '@klear/schema/documents'
import { wireValidationErrorMapFromTrpcError } from '@klear/schema/wire/trpc-form-errors'
import { Alert, AlertDescription } from '@klear/ui/components/alert'
import { Button } from '@klear/ui/components/button'
import { Skeleton } from '@klear/ui/components/skeleton'
import { ApproveRow, APPROVE_KEY, canDecideStep, holdReason } from '@/app/document-review/approve-row'
import { BlockedPanel, readBlock } from '@/app/document-review/blocked-panel'
import { ArrivalProvenance } from '@/app/document-review/email-provenance'
import { CommitBar } from '@/app/document-review/commit-bar'
import { ApproveModal } from '@/app/document-review/approve-modal'
import { RejectModal } from '@/app/document-review/reject-modal'
import { DocumentPane } from '@/app/document-review/document-pane'
import { FieldEditor } from '@/app/document-review/field-editor'
import { OrderEditor } from '@/app/document-review/order-editor'
import { supplierEdit } from '@/app/document-review/party-edit'
import { PartyPicker } from '@/app/document-review/party-picker'
import { DeniedOutcome, FiledOutcome, UploadOutcome } from '@/app/document-review/outcome-panel'
import { filedAsUpload } from '@/app/document-review/filed-records'
import { CreatingPanel, ProcessingPanel } from '@/app/document-review/processing-panel'
import { draftFrom, hintsFrom, type CarriedHints, type PremiseDraft } from '@/app/document-review/premise'
import { ProcessAgainEditor } from '@/app/document-review/process-again-editor'
import { RunSelect } from '@/app/document-review/run-select'
import { PlanHeaderCard } from '@/app/document-review/plan-header-card'
import { ProposalCard } from '@/app/document-review/proposal-card'
import { reviewMode } from '@/app/document-review/review-mode'
import {
  ORDER_KEY,
  TRADER_KEY,
  approvalItems,
  proposalOrdinal,
  valueSignature,
  type ApprovalItem,
  type FieldItem,
  type OrderItem,
  type SupplierItem,
  type TraderItem
} from '@/app/document-review/approval-items'
import {
  DATE_FIELDS,
  fieldEditIssue,
  normaliseCommittedValue,
  normaliseEditValue
} from '@/app/document-review/field-edit'
import { proposalFieldLabel, recordFieldLabel } from '@/app/document-review/record-card-specs'
import { draftFieldErrors, unmappedErrorMessages } from '@/app/document-review/update-error-paths'
import { useActiveApprovalKey } from '@/app/document-review/use-active-approval-key'
import { useApprovalKeyboard } from '@/app/document-review/use-approval-keyboard'
import { useConfirmations } from '@/app/document-review/use-confirmations'
import { useDocumentReviewActions } from '@/app/document-review/use-document-review-actions'
import { useReviewPeople } from '@/app/document-review/use-review-people'
import {
  useDocumentReviewDetail,
  type DocumentReviewDetailOutput,
  type DocumentReviewUpdateInput
} from '@/app/document-review/use-document-review'
import { latestRun, newerRunOf } from '@/app/document-review/review-runs'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

export type DocumentReviewWorkspaceProps = {
  processingId: string
  canEdit: boolean
  traders: readonly { id: number; name: string | null }[]
}

type Editing = { key: string; value: string; amountKind?: ContributionAmountKind | null }

type PartyRow = SupplierItem | OrderItem | TraderItem
type EditableItem = FieldItem | PartyRow

function editableItem(item: ApprovalItem | undefined): EditableItem | null {
  if (item === undefined) return null
  return item.type === 'field' || item.type === 'supplier' || item.type === 'order' || item.type === 'trader'
    ? item
    : null
}

function carriedHints(items: readonly ApprovalItem[], isConfirmed: (key: string) => boolean): CarriedHints {
  const carried: CarriedHints = { confirmed: [], answers: [], orderConfirmed: false }
  for (const item of items) {
    if (!isConfirmed(item.key)) continue
    if (item.type === 'order') carried.orderConfirmed = true
    if (item.type === 'field' && item.value !== null) {
      carried.confirmed.push({
        reference: item.proposal.externalReference,
        kind: item.proposal.kind,
        field: item.field,
        value: item.value
      })
    }
    const answered =
      item.type === 'doubt'
        ? [item.doubt]
        : item.type === 'order'
          ? item.doubts.filter((doubt) => doubt.kind !== 'order_missing')
          : []
    for (const doubt of answered) {
      carried.answers.push({
        doubtKind: doubt.kind,
        field: doubt.field,
        message: doubt.message,
        answer: doubt.proposedAnswer
      })
    }
  }
  return carried
}

function supplierSeed(item: SupplierItem): string {
  const record = item.proposal.record
  const supplier = record !== null && record.kind === 'create' ? record.supplier : null
  return supplier?.kind === 'create' ? supplier.party.name : (item.proposal.supplierName ?? '')
}

function pickSignature(detail: DocumentReviewDetailOutput, item: SupplierItem | OrderItem): string {
  if (item.type === 'order') return JSON.stringify(detail.order)
  const record = detail.proposals.find((one) => one.ordinal === item.proposal.ordinal)?.record
  return JSON.stringify(record?.kind === 'create' ? record.supplier : null)
}

type ProposalEdit = DocumentReviewUpdateInput['data']['proposals'] extends (infer T)[] | undefined ? T : never

function refusalMessage(caught: unknown, at: { ordinal: number | null; field: string | null }): string {
  const errorMap = wireValidationErrorMapFromTrpcError(caught)
  if (errorMap === undefined) return isError(caught) ? caught.message : 'The correction was refused.'
  const ordinals = at.ordinal === null ? [] : [at.ordinal]
  const fieldErrors = draftFieldErrors(errorMap, ordinals)
  const parts = [
    ...fieldErrors.map((one) => (one.field === at.field ? one.message : `${one.field}: ${one.message}`)),
    ...unmappedErrorMessages(errorMap, ordinals)
  ]
  return parts.length === 0 ? 'The correction was refused.' : parts.join(' ')
}

function recordCurrency(item: FieldItem): string | null {
  const record = item.proposal.record
  return record !== null && record.kind === 'create' ? (record.fields.currency ?? null) : null
}

type RecordUpdatesEdit = NonNullable<ProposalEdit['updates']>

function updatesOf(item: FieldItem): RecordUpdatesEdit | null {
  const record = item.proposal.record
  return record?.kind === 'attach' && record.updates !== undefined && item.field in record.updates
    ? record.updates
    : null
}

function updatesWithout(updates: RecordUpdatesEdit, field: string): RecordUpdatesEdit | null {
  const rest = omit(updates, field)
  return isEmpty(rest) ? null : rest
}

function editFor(item: FieldItem, value: string | null, amountKind?: ContributionAmountKind | null): ProposalEdit {
  const updates = updatesOf(item)
  if (updates !== null) {
    const next =
      value === null && (DATE_FIELDS.includes(item.field) || item.field === 'amount')
        ? updatesWithout(updates, item.field)
        : { ...updates, [item.field]: value }
    return { ordinal: item.proposal.ordinal, updates: next }
  }
  const edit: Record<string, string | number | null> = {
    ordinal: item.proposal.ordinal,
    [item.field]: value,
    ...(amountKind != null && value !== null && { amountKind })
  }
  if (item.field === 'amount' && value !== null && recordCurrency(item) !== null) {
    const decoded = decodeMoneyWire(value)
    if (decoded !== undefined) edit['currency'] = decoded.currency
  }
  return edit as ProposalEdit
}

function currencyFollowsAmountIssue(item: FieldItem, value: string | null): string | null {
  if (item.field !== 'currency' || value === null) return null
  const record = item.proposal.record
  const amount = record !== null && record.kind === 'create' ? record.fields.amount : null
  if (amount === null) return null
  const decoded = decodeMoneyWire(amount)
  if (decoded === undefined || decoded.currency === value.toUpperCase()) return null
  return `The amount is stated in ${decoded.currency}, and a record holds one currency. Correct the amount instead.`
}

type Following = { fromRun: number; traderName: string | null }

export function DocumentReviewWorkspace({ processingId, canEdit, traders }: DocumentReviewWorkspaceProps) {
  const navigate = useNavigate()
  const [following, setFollowing] = useState<Following | null>(null)
  const [premise, setPremise] = useState<PremiseDraft | null>(null)
  const { approve, deny, update, process, fileAsUpload } = useDocumentReviewActions()
  const { data: detail, isPending, error } = useDocumentReviewDetail(processingId, { poll: following !== null })
  useDocumentTitle(detail?.uploadedFilename, 'Documents', detail?.traderName)
  const people = useReviewPeople(detail)
  const confirmations = useConfirmations(detail?.run)
  const [fileUploadOpen, setFileUploadOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [editing, setEditing] = useState<Editing | null>(null)
  const [issues, setIssues] = useState<Record<string, string>>({})

  const { touched } = confirmations
  const items = useMemo(() => (detail === undefined ? [] : approvalItems(detail, touched)), [detail, touched])
  const itemByKey = useMemo(() => new Map(items.map((item) => [item.key, item])), [items])
  const keys = useMemo(() => [...items.map((item) => item.key), APPROVE_KEY], [items])
  const approvable = useMemo(() => items.filter((item) => item.needsApproval), [items])
  const highlights = useMemo(
    () =>
      items.flatMap((item) => {
        if (item.type !== 'field' || item.extraction === null) return []
        const { confidence, sources } = item.extraction
        return sources.map((source, rank) => ({
          key: item.key,
          page: source.page,
          rank,
          quote: source.quote,
          confidence
        }))
      }),
    [items]
  )

  const isConfirmed = (key: string) => {
    const item = itemByKey.get(key)
    return item !== undefined && confirmations.isConfirmed(key, item.signature)
  }
  const confirmedCount = approvable.filter((item) => isConfirmed(item.key)).length

  const step = detail?.step ?? 'processing'
  const mode = reviewMode(step)
  const settled = mode === 'filed' || mode === 'denied'
  const neverProcessed = detail?.model === null && detail.proposals.length === 0
  const stillProcessing = mode === 'processing'
  const block = detail === undefined || mode !== 'blocked' ? null : readBlock(detail)
  const canCorrect = canEdit && canDecideStep(step)
  const readOnly = settled || !canCorrect

  const isOpen = (key: string) => {
    const item = itemByKey.get(key)
    return item !== undefined && item.needsApproval && !isConfirmed(key)
  }

  const clearIssue = (key: string) => setIssues((current) => omit(current, key))

  const openRun = (id: string) => void navigate(`/${ROUTE_DOCUMENT_REVIEW}/${id}`)

  useEffect(() => {
    if (following === null || detail === undefined) return
    const newer = detail.runs.filter((run) => run.run > following.fromRun).sort((a, b) => b.run - a.run)[0]
    if (newer !== undefined) void navigate(`/${ROUTE_DOCUMENT_REVIEW}/${newer.id}`)
  }, [following, detail, navigate])

  const queueReprocess = (traderId?: number, hints?: ReviewerHints) => {
    if (detail === undefined) return
    process.mutate(
      { processingId: detail.id, ...(traderId !== undefined && { traderId }), ...(hints !== undefined && { hints }) },
      {
        onSuccess: (result) => {
          setPremise(null)
          setEditing(null)
          if (result.outcome === 'queued') {
            const traderName =
              traderId === undefined ? detail.traderName : (traders.find((one) => one.id === traderId)?.name ?? null)
            setFollowing({ fromRun: latestRun(detail), traderName })
            return
          }
          if (result.outcome !== 'deduped') return
          const underway = detail.runs.find(
            (run) => run.traderId === (traderId ?? detail.traderId) && isUnderwayDocumentProcessingStatus(run.status)
          )
          if (underway !== undefined && underway.id !== detail.id) openRun(underway.id)
        }
      }
    )
  }

  const startEdit = (item: EditableItem) => {
    if (!canCorrect) return
    if (detail === undefined) return
    if (item.type !== 'field' && item.type !== 'trader' && detail.traderId == null) return
    clearIssue(item.key)
    if (item.type === 'field') {
      if (!item.editable) return
      setEditing({
        key: item.key,
        value: item.value ?? '',
        ...(item.amountKind !== undefined && {
          amountKind: item.amountKind ?? (item.value === null ? null : 'change')
        })
      })
      return
    }
    if (item.type === 'trader') {
      setPremise(draftFrom(detail))
    }
    setEditing({ key: item.key, value: '' })
  }

  const confirmKey = (key: string) => {
    const item = itemByKey.get(key)
    if (item === undefined || !item.needsApproval || readOnly) return
    if ((item.type === 'field' && item.required && item.value === null) || (item.type === 'order' && item.required)) {
      startEdit(item)
      return
    }
    confirmItem(key, item.signature)
  }

  const toggleKey = (key: string) => {
    const item = itemByKey.get(key)
    if (item === undefined || !item.needsApproval || readOnly) return
    if (isConfirmed(key)) {
      confirmations.unconfirm(key)
      return
    }
    confirmKey(key)
  }

  const editKey = (key: string) => {
    const item = editableItem(itemByKey.get(key))
    if (item !== null) startEdit(item)
  }

  const supersedingRun = detail === undefined ? null : newerRunOf(detail)

  const hold =
    detail === undefined
      ? null
      : holdReason({
          step,
          pendingParent: detail.pendingParent,
          canEdit,
          editing: editing !== null
        })
  const outstanding = approvable.length - confirmedCount
  const approveEnabled = detail !== undefined && block === null && hold === null && outstanding === 0

  const createRecords = () => {
    if (detail !== undefined && !approve.isPending) approve.mutate({ processingId: detail.id, confirmed: true })
  }

  const confirmItem = (key: string, signature: string) => {
    const closesTheLast =
      detail !== undefined &&
      block === null &&
      outstanding === 1 &&
      isOpen(key) &&
      holdReason({ step, pendingParent: detail.pendingParent, canEdit, editing: false }) === null
    confirmations.confirm(key, signature)
    if (closesTheLast) createRecords()
  }

  const rowFocus = useActiveApprovalKey()

  const keyboard = useApprovalKeyboard({
    keys,
    isOpen,
    finishKey: APPROVE_KEY,
    onConfirm: confirmKey,
    onToggle: toggleKey,
    onEdit: editKey,
    onFinish: () => {
      if (approveEnabled) createRecords()
      else keyboard.focusNextOpen()
    }
  })

  const cancelEdit = () => {
    if (editing === null) return
    const key = editing.key
    setEditing(null)
    setPremise(null)
    window.setTimeout(() => keyboard.focusKey(key), 0)
  }

  const commitParty = async (item: SupplierItem | OrderItem, data: DocumentReviewUpdateInput['data']) => {
    if (update.isPending || process.isPending) return
    try {
      const next = await update.mutateAsync({ processingId, data })
      clearIssue(item.key)
      confirmItem(item.key, pickSignature(next, item))
      setEditing(null)
      keyboard.focusAfter(item.key)
    } catch (caught) {
      setIssues((current) => ({
        ...current,
        [item.key]: refusalMessage(caught, { ordinal: proposalOrdinal(item), field: null })
      }))
    }
  }

  const submitPremise = () => {
    if (detail === undefined || premise === null || premise.traderId === null) return
    const hints = hintsFrom(detail, premise, carriedHints(items, isConfirmed))
    queueReprocess(premise.traderId === detail.traderId ? undefined : premise.traderId, hints)
  }

  const premiseEditor = () => {
    if (detail === undefined || premise === null) return null
    const hints = hintsFrom(detail, premise, carriedHints(items, isConfirmed))
    return (
      <ProcessAgainEditor
        draft={premise}
        keptCount={(hints.confirmed?.length ?? 0) + (hints.answers?.length ?? 0)}
        traders={traders}
        isPending={process.isPending}
        onChange={(draft) => setPremise((current) => (current === null ? null : draft))}
        onSubmit={submitPremise}
        onCancel={cancelEdit}
      />
    )
  }

  const pickerFor = (item: SupplierItem | OrderItem) => {
    if (editing === null || detail === undefined || detail.traderId == null) return null
    const traderName = detail.traderName ?? 'the trader'
    const disabled = update.isPending || process.isPending
    if (item.type === 'order') {
      return (
        <OrderEditor
          detail={detail}
          traderId={detail.traderId}
          traderName={traderName}
          isPending={disabled}
          onSave={(data) => void commitParty(item, data)}
          onCancel={cancelEdit}
        />
      )
    }
    return (
      <PartyPicker
        kind="supplier"
        traderId={detail.traderId}
        traderName={traderName}
        seed={supplierSeed(item)}
        disabled={disabled}
        onPick={(pick) => void commitParty(item, supplierEdit(item.proposal.ordinal, pick))}
        onCancel={cancelEdit}
      />
    )
  }

  const commitEdit = async ({ leaving }: { leaving: boolean }) => {
    if (editing === null || update.isPending) return
    const item = itemByKey.get(editing.key)
    if (item?.type !== 'field') return
    const value = normaliseCommittedValue(item.field, normaliseEditValue(editing.value))
    const amountKind = editing.amountKind
    const unchanged = value === item.value && amountKind === item.amountKind
    const close = () => setEditing((current) => (current?.key === item.key ? null : current))
    if (leaving && unchanged) {
      clearIssue(item.key)
      close()
      return
    }
    const issue =
      value === null && item.required
        ? `${recordFieldLabel(item.proposal.kind, item.field)} is required to create this record.`
        : (fieldEditIssue(item.field, value) ??
          currencyFollowsAmountIssue(item, value) ??
          (value !== null && amountKind === null ? 'Choose Total or Change.' : null))
    if (issue !== null) {
      setIssues((current) => ({ ...current, [item.key]: issue }))
      return
    }
    const settle = () => {
      clearIssue(item.key)
      confirmItem(item.key, valueSignature(value, amountKind))
      close()
      if (!leaving) keyboard.focusAfter(item.key)
    }
    if (unchanged) {
      settle()
      return
    }
    try {
      await update.mutateAsync({ processingId, data: { proposals: [editFor(item, value, amountKind)] } })
      settle()
    } catch (caught) {
      setIssues((current) => ({
        ...current,
        [item.key]: refusalMessage(caught, { ordinal: item.proposal.ordinal, field: item.field })
      }))
    }
  }

  const keepCurrentValue = async (item: FieldItem, updates: RecordUpdatesEdit) => {
    if (update.isPending) return
    try {
      await update.mutateAsync({
        processingId,
        data: { proposals: [{ ordinal: item.proposal.ordinal, updates: updatesWithout(updates, item.field) }] }
      })
      clearIssue(item.key)
      setEditing(null)
    } catch (caught) {
      setIssues((current) => ({
        ...current,
        [item.key]: refusalMessage(caught, { ordinal: item.proposal.ordinal, field: item.field })
      }))
    }
  }

  if (error !== null) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <BackLink />
        <Alert variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    )
  }

  if (isPending || detail === undefined) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <BackLink />
        <Skeleton className="h-full w-full" />
      </div>
    )
  }

  const traderItem = itemByKey.get(TRADER_KEY)
  const orderItem = itemByKey.get(ORDER_KEY)
  const issueFor = (key: string) => issues[key] ?? null
  const documentName = detail.uploadedFilename ?? 'The document'

  const fileUpload = () => {
    fileAsUpload.mutate(
      { processingId: detail.id, confirmed: true },
      {
        onSuccess: (filed) => {
          setFileUploadOpen(false)
          openRun(filed.id)
        }
      }
    )
  }

  const submitReject = (input: { reason: string }) => {
    deny.mutate(
      { processingId: detail.id, reason: input.reason },
      {
        onSuccess: () => {
          setRejectOpen(false)
        }
      }
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <div className="flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <BackLink />
        <h1 className="text-xl font-semibold">{detail.uploadedFilename ?? 'Source document'}</h1>
        <RunSelect runs={detail.runs} currentId={detail.id} currentStep={step} onChange={openRun} />
        <span className="text-muted-foreground font-mono text-xs">{detail.id}</span>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[52fr_48fr]">
        <div className="min-h-0 lg:h-full">
          <DocumentPane
            documentId={detail.documentId}
            uploadedFilename={detail.uploadedFilename}
            highlights={highlights}
            activeKey={rowFocus.activeKey}
          />
        </div>

        <div className="flex min-h-0 flex-col gap-3">
          {mode === 'creating' && <CreatingPanel detail={detail} />}
          {mode === 'filed' &&
            (filedAsUpload(detail) ? (
              <UploadOutcome detail={detail} names={people} />
            ) : (
              <FiledOutcome detail={detail} names={people} />
            ))}
          {mode === 'denied' && (
            <DeniedOutcome
              detail={detail}
              names={people}
              canEdit={canEdit}
              isProcessing={process.isPending}
              isFiling={fileAsUpload.isPending}
              onReprocess={() => queueReprocess()}
              onFileAsUpload={() => setFileUploadOpen(true)}
            />
          )}

          {following !== null && (
            <Alert className="shrink-0">
              <AlertDescription className="flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />
                Queued{following.traderName === null ? '' : ` for ${following.traderName}`}. Opening it…
              </AlertDescription>
            </Alert>
          )}

          {supersedingRun !== null && (
            <Alert className="shrink-0">
              <AlertDescription>
                A newer review is available.
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-2"
                  onClick={() => {
                    const newest = detail.runs.find((run) => run.run === supersedingRun)
                    if (newest !== undefined) openRun(newest.id)
                  }}
                >
                  Open it
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {neverProcessed && (
            <section className="bg-card shrink-0 rounded-lg border">
              <ArrivalProvenance arrival={detail} />
            </section>
          )}

          {!neverProcessed && (
            <div
              ref={keyboard.containerRef}
              onKeyDown={keyboard.onKeyDown}
              onFocus={rowFocus.onFocus}
              onBlur={rowFocus.onBlur}
              onMouseOver={rowFocus.onMouseOver}
              onMouseLeave={rowFocus.onMouseLeave}
              className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1"
            >
              {settled && (
                <p className="text-muted-foreground shrink-0 text-xs">
                  {mode === 'filed' ? 'What the run filed' : 'What the run proposed'}
                </p>
              )}
              {stillProcessing ? (
                <ProcessingPanel detail={detail} />
              ) : block !== null ? (
                <BlockedPanel
                  reading={block}
                  arrival={detail}
                  traders={traders}
                  canEdit={canEdit}
                  isProcessing={process.isPending}
                  isFiling={fileAsUpload.isPending}
                  onReprocess={queueReprocess}
                  onFileAsUpload={() => setFileUploadOpen(true)}
                  onReject={() => setRejectOpen(true)}
                />
              ) : (
                <>
                  {traderItem?.type !== 'trader' || orderItem?.type !== 'order' ? (
                    <section className="bg-card rounded-lg border">
                      <ArrivalProvenance arrival={detail} />
                    </section>
                  ) : (
                    <PlanHeaderCard
                      detail={detail}
                      premise={premise}
                      traderItem={traderItem}
                      orderItem={orderItem}
                      readOnly={readOnly}
                      isConfirmed={isConfirmed}
                      editingKey={editing?.key ?? null}
                      editorFor={(item) => (item.type === 'trader' ? premiseEditor() : pickerFor(item))}
                      issueFor={issueFor}
                      onToggle={toggleKey}
                      onEdit={startEdit}
                    />
                  )}
                  {detail.proposals.map((proposal) => (
                    <ProposalCard
                      key={proposal.ordinal}
                      proposal={proposal}
                      proposals={detail.proposals}
                      items={items.filter((item) => proposalOrdinal(item) === proposal.ordinal)}
                      readOnly={readOnly}
                      isConfirmed={isConfirmed}
                      issueFor={issueFor}
                      editingKey={editing?.key ?? null}
                      editorFor={(item) => {
                        if (item.type === 'supplier') return pickerFor(item)
                        const updates = updatesOf(item)
                        return (
                          <FieldEditor
                            field={item.field}
                            label={proposalFieldLabel(item.proposal, item.field)}
                            value={editing?.value ?? ''}
                            disabled={update.isPending}
                            onChange={(value) =>
                              setEditing((current) => (current === null ? null : { ...current, value }))
                            }
                            onCommit={() => void commitEdit({ leaving: false })}
                            onLeave={() => void commitEdit({ leaving: true })}
                            onCancel={cancelEdit}
                            {...(updates !== null && { onKeep: () => void keepCurrentValue(item, updates) })}
                            {...(editing?.amountKind !== undefined && {
                              amountKind: {
                                value: editing.amountKind,
                                onChange: (amountKind) =>
                                  setEditing((current) => (current === null ? null : { ...current, amountKind }))
                              }
                            })}
                          />
                        )
                      }}
                      onToggle={toggleKey}
                      onEdit={startEdit}
                    />
                  ))}
                  {!settled && (
                    <ApproveRow
                      holdReason={hold}
                      outstanding={outstanding}
                      approvableCount={approvable.length}
                      canEdit={canEdit}
                      step={step}
                      isApproving={approve.isPending}
                      isRejecting={deny.isPending}
                      onApprove={createRecords}
                      onReject={() => setRejectOpen(true)}
                      onNextOpen={keyboard.focusNextOpen}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {block === null && !settled && !stillProcessing && <CommitBar detail={detail} canEdit={canEdit} />}
        </div>
      </div>

      <ApproveModal
        open={fileUploadOpen}
        isPending={fileAsUpload.isPending}
        title="File as an upload"
        pendingLabel="Filing…"
        entries={[{ action: 'Files', what: `${documentName} as an upload`, amount: null }]}
        notes={[]}
        onOpenChange={setFileUploadOpen}
        onApprove={fileUpload}
      />
      <RejectModal open={rejectOpen} isPending={deny.isPending} onOpenChange={setRejectOpen} onReject={submitReject} />
    </div>
  )
}

export function BackLink() {
  return (
    <Link
      to={`/${ROUTE_DOCUMENTS}?status=${'needs_review' satisfies DocumentStatus}`}
      className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1 text-sm"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      Documents
    </Link>
  )
}
